import React, { useState, useEffect } from 'react';
import { Button, Form, FormGroup, FormLabel, FormControl, FormCheck, ListGroup, ListGroupItem, Pagination, InputGroup, Row, Col, Container, Nav } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import '../AdminDashboard.css';
import { isAdmin, hasPermission } from '../utils/permissions';
import axios from 'axios';
import PricingPlanManagement from './PricingPlanManagement';
import PublishManagerModal from '../cognitive-flows/components/dashboard/PublishManagerModal';
import WorkspaceAgentsManager from '../cognitive-flows/components/dashboard/WorkspaceAgentsManager';
import McpServerToolsManager from '../cognitive-flows/components/dashboard/McpServerToolsManager';
import DefaultSourcesManager from '../cognitive-flows/components/dashboard/DefaultSourcesManager';
import CanvasPublishManager from '../cognitive-flows/components/dashboard/CanvasPublishManager';
import AbPasscodeManager from '../components/abTesting/AbPasscodeManager';
import '../components/abTesting/abTesting.css';
import { setCognitiveFlowsApiBase } from '../cognitive-flows/utils/api';
import { showToast as toast } from '../toastManager';
import { OpenAiProjectDialog, OpenAiProjectFields, openAiChoiceBody, openAiChoiceComplete, useOpenAiAssignments, useOpenAiProjects } from '../components/admin/OpenAiProjectControls';
 
// The shop key the OpenAI project assignments are stored under (lowercase, no .myshopify.com).
const normalizeAccountShop = (shop) => String(shop || '').trim().toLowerCase().replace(/\.myshopify\.com$/, '');

// Create Account → OpenAI project (2026-10-09 b): the projects load when the form opens.
const CreateAccountOpenAiField = ({ appUrl, value, onChange, accountName }) => {
  const { projects, configured, loading } = useOpenAiProjects(appUrl);
  return <OpenAiProjectFields value={value} onChange={onChange} projects={projects} configured={configured} loading={loading} accountName={accountName} idPrefix="openai-create" />;
};

const AdminDashboard = ({ ViewedPage, app_url }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('create-account');
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [createAccountLoading, setCreateAccountLoading] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [cloneWebsiteLoading, setCloneWebsiteLoading] = useState(false);
  const [clonedUrls, setClonedUrls] = useState([]);
  const [clonedUrlsLoading, setClonedUrlsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Search and filter states
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');
  const [userSortBy, setUserSortBy] = useState('');
  const [accountSortBy, setAccountSortBy] = useState('');
  const [clonedUrlsSearchTerm, setClonedUrlsSearchTerm] = useState('');
  const [clonedUrlsSortBy, setClonedUrlsSortBy] = useState('');
  const [selectedClientForUrls, setSelectedClientForUrls] = useState('');

  // Separate state for each tab to prevent conflicts
  const [createAccountData, setCreateAccountData] = useState({
    shop: '',
    shop_name: '',
    account_type: 'live',
    owner_email: ''
  });
  // The OpenAI project a new account runs on (2026-10-09 b): the shared default
  // unless a dedicated one is chosen.
  const [createOpenAi, setCreateOpenAi] = useState({ mode: 'default', projectId: '', projectName: '' });
  const [openAiDialogAccount, setOpenAiDialogAccount] = useState(null);
  const [openAiRefresh, setOpenAiRefresh] = useState(0);
  const openAiAssignments = useOpenAiAssignments(app_url, openAiRefresh);

  const [createUserData, setCreateUserData] = useState({
    email: '',
    role: 'user',
    permissions: [],
    dev_page_permissions: []
  });

  const [cloneWebsiteData, setCloneWebsiteData] = useState({
    url: '',
    client: '',
    selectedCloner: 'standard'
  });



  // Available pages for access control - will be fetched from MongoDB
  const [availablePages, setAvailablePages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(true);

  // Dev page mapping for developer role
  const [allDevPageMapping, setAllDevPageMapping] = useState(null);
  const [devPageMappingLoading, setDevPageMappingLoading] = useState(false);

  // Available roles
  const availableRoles = ['user', 'admin', 'developer'];

  // Available permissions
  const availablePermissions = [];

  // Admin sections with tab structure (similar to Settings.jsx)
  const adminSections = [
    { id: 'create-account', title: 'Accounts', component: 'accounts' },
    { id: 'create-user', title: 'Users', component: 'users' },
    { id: 'clone-website', title: 'Clone Website', component: 'clone-website' },
    { id: 'pricing-plans', title: 'Pricing Plans', component: 'pricing-plans' },
    { id: 'prebuilt-agents', title: 'Prebuilt Agents', component: 'prebuilt-agents' },
    { id: 'workspace-agents', title: 'Workspace Agents', component: 'workspace-agents' },
    { id: 'mcp-tools', title: 'MCP Tools', component: 'mcp-tools' },
    { id: 'canvas-templates', title: 'Canvas Templates', component: 'canvas-templates' },
    { id: 'default-data-sources', title: 'Default Data Sources', component: 'default-data-sources' },
    { id: 'enable-analytics-extension', title: 'Enable Analytics Extension', component: 'enable-analytics-extension' },
    { id: 'ab-testing', title: 'A/B Testing', component: 'ab-testing' }
  ];

  // State to control visibility of the create account form
  const [showCreateAccountForm, setShowCreateAccountForm] = useState(false);
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [showCloneWebsiteForm, setShowCloneWebsiteForm] = useState(false);

  // Analytics Extension state
  const [analyticsShop, setAnalyticsShop] = useState('');
  const [analyticsLandingPages, setAnalyticsLandingPages] = useState([]);
  const [selectedAnalyticsLPs, setSelectedAnalyticsLPs] = useState([]);
  const [enabledAnalyticsExtensions, setEnabledAnalyticsExtensions] = useState({ is_active: false, config_id: null });
  const [analyticsExtensionLoading, setAnalyticsExtensionLoading] = useState(false);
  const [analyticsLPLoading, setAnalyticsLPLoading] = useState(false);

  useEffect(() => {
    ViewedPage(101);
    fetchData();
    fetchAvailablePages();
  }, [ViewedPage]);

  // Point the Cognitive Flows API client at this environment so the embedded
  // Prebuilt Agents manager (admin cross-store publishing) resolves correctly.
  useEffect(() => {
    if (app_url) setCognitiveFlowsApiBase(app_url);
  }, [app_url]);

  // Fetch dev page mapping when form is shown and role is developer
  useEffect(() => {
    if (showCreateUserForm && createUserData.role === 'developer' && !allDevPageMapping && !devPageMappingLoading) {
      fetchAllDevPageMapping();
    }
  }, [showCreateUserForm, createUserData.role]);

  // Set default dev page permissions when mapping is loaded and role is developer
  useEffect(() => {
    if (createUserData.role === 'developer' && allDevPageMapping && typeof allDevPageMapping === 'object' &&
        (!createUserData.dev_page_permissions || createUserData.dev_page_permissions.length === 0)) {
      const pageNames = Object.keys(allDevPageMapping);
      setCreateUserData(prev => ({
        ...prev,
        dev_page_permissions: pageNames
      }));
    }
  }, [allDevPageMapping, createUserData.role]);

  // Reset pagination when switching tabs
  useEffect(() => {
    setCurrentPage(1);
    // Reset search and sort states when switching tabs
    setUserSearchTerm('');
    setAccountSearchTerm('');
    setAccountTypeFilter('');
    setClonedUrlsSearchTerm('');
    setUserSortBy('');
    setAccountSortBy('');
    setClonedUrlsSortBy('');
    setSelectedClientForUrls('');
    setClonedUrls([]);
  }, [activeTab]);

  // Reset form data when switching tabs
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setMessage({ type: '', text: '' });
    setCurrentPage(1);
    
    // Reset form visibility states
    setShowCreateAccountForm(false);
    setShowCreateUserForm(false);
    setShowCloneWebsiteForm(false);
    
    // Reset form data based on the tab being switched to
    if (tabName === 'create-account') {
      setCreateAccountData({ shop: '', shop_name: '', account_type: 'live', owner_email: '' });
    } else if (tabName === 'create-user') {
      setCreateUserData({ email: '', role: 'user', permissions: [], dev_page_permissions: [] });
      setAllDevPageMapping(null);
    } else if (tabName === 'clone-website') {
      setCloneWebsiteData({ url: '', client: '', selectedCloner: 'standard' });
    }
  };

  const fetchAvailablePages = async () => {
    try {
      setPagesLoading(true);
      const response = await axios.get(app_url + '/api/admin/available-pages', { withCredentials: true });
      const data = response.data;

      let pagesMapping = null;

      // Handle the server response format
      if (data.success && data.data && data.data.all_pages_mapping) {
        pagesMapping = data.data.all_pages_mapping;
      } else if (data.all_pages_mapping) {
        // Fallback for direct response format
        pagesMapping = data.all_pages_mapping;
      }

      if (pagesMapping && typeof pagesMapping === 'object') {
        const pagesArray = Object.keys(pagesMapping);
        setAvailablePages(pagesArray);
      } else {
        setAvailablePages([]);
      }
    } catch (error) {
      console.error('Error fetching available pages:', error);
      // Fallback to default pages if API fails
      setAvailablePages([]);
    } finally {
      setPagesLoading(false);
    }
  };

  const fetchAllDevPageMapping = async () => {
    try {
      setDevPageMappingLoading(true);
      const response = await axios.get(app_url + '/api/admin/all-dev-page-mapping', { withCredentials: true });
      const data = response.data;
      
      if (data.success && data.data && data.data.all_dev_page_mapping) {
        const devPageMapping = data.data.all_dev_page_mapping;
        setAllDevPageMapping(devPageMapping);
        // Default all pages checked when role is developer
        if (createUserData.role === 'developer' && devPageMapping && typeof devPageMapping === 'object') {
          const pageNames = Object.keys(devPageMapping);
          setCreateUserData(prev => ({
            ...prev,
            dev_page_permissions: pageNames
          }));
        }
      } else {
        setAllDevPageMapping(null);
      }
    } catch (error) {
      console.error('Error fetching dev page mapping:', error);
      setAllDevPageMapping(null);
    } finally {
      setDevPageMappingLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setFetchingData(true);
      
      // Fetch users
      const usersResponse = await axios.get(app_url + '/api/admin/users', { withCredentials: true });
      const usersData = usersResponse.data;
      if (usersData.success) {
        setUsers(usersData.data);
      }

      // Fetch stores
      const storesResponse = await axios.get(app_url + '/api/admin/stores', { withCredentials: true });
      const storesData = storesResponse.data;
      if (storesData.success) {
        setStores(storesData.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Failed to fetch data' });
    } finally {
      setFetchingData(false);
    }
  };

  const fetchClonedUrls = async (client) => {
    if (!client) return;
    
    try {
      setClonedUrlsLoading(true);
      const response = await axios.get(app_url + `/api/admin/cloned-urls?client=${encodeURIComponent(client)}`, { withCredentials: true });
      const data = response.data;
      if (data.success) {
        setClonedUrls(data.data);
      } else {
        setClonedUrls([]);
      }
    } catch (error) {
      console.error('Error fetching cloned URLs:', error);
      setClonedUrls([]);
    } finally {
      setClonedUrlsLoading(false);
    }
  };

  const handleClientSelectionForUrls = (client) => {
    setSelectedClientForUrls(client);
    if (client) {
      fetchClonedUrls(client);
    } else {
      setClonedUrls([]);
    }
  };

  // Filter and search functions
  const getFilteredUsers = () => {
    let filtered = users;

    // Apply search filter
    if (userSearchTerm) {
      filtered = filtered.filter(user => 
        user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        (user.role && user.role.toLowerCase().includes(userSearchTerm.toLowerCase()))
      );
    }

    // Apply sorting
    if (userSortBy) {
      filtered = [...filtered].sort((a, b) => {
        switch (userSortBy) {
          case 'email-asc':
            return a.email.localeCompare(b.email);
          case 'email-desc':
            return b.email.localeCompare(a.email);
          case 'role-asc':
            return (a.role || 'user').localeCompare(b.role || 'user');
          case 'role-desc':
            return (b.role || 'user').localeCompare(a.role || 'user');
          case 'accounts-asc':
            return (a.stores?.length || 0) - (b.stores?.length || 0);
          case 'accounts-desc':
            return (b.stores?.length || 0) - (a.stores?.length || 0);
          default:
            return 0;
        }
      });
    }

    return filtered;
  };

  const getFilteredAccounts = () => {
    let filtered = stores;

    // Apply search filter
    if (accountSearchTerm) {
      filtered = filtered.filter(store => 
        store.shop.toLowerCase().includes(accountSearchTerm.toLowerCase()) ||
        (store.shop_name && store.shop_name.toLowerCase().includes(accountSearchTerm.toLowerCase()))
      );
    }

    // Apply account type filter
    if (accountTypeFilter) {
      filtered = filtered.filter(store => 
        store.account_type === accountTypeFilter
      );
    }

    // Apply sorting
    if (accountSortBy) {
      filtered = [...filtered].sort((a, b) => {
        switch (accountSortBy) {
          case 'name-asc':
            return (a.shop_name || a.shop).localeCompare(b.shop_name || b.shop);
          case 'name-desc':
            return (b.shop_name || b.shop).localeCompare(a.shop_name || a.shop);
          case 'shop-asc':
            return a.shop.localeCompare(b.shop);
          case 'shop-desc':
            return b.shop.localeCompare(a.shop);
          case 'features-asc':
            const aFeatures = a.allowed_pages ? Object.keys(a.allowed_pages).filter(page => a.allowed_pages[page]).length : 0;
            const bFeatures = b.allowed_pages ? Object.keys(b.allowed_pages).filter(page => b.allowed_pages[page]).length : 0;
            return aFeatures - bFeatures;
          case 'features-desc':
            const aFeaturesDesc = a.allowed_pages ? Object.keys(a.allowed_pages).filter(page => a.allowed_pages[page]).length : 0;
            const bFeaturesDesc = b.allowed_pages ? Object.keys(b.allowed_pages).filter(page => b.allowed_pages[page]).length : 0;
            return bFeaturesDesc - aFeaturesDesc;
          case 'created-asc':
            return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
          case 'created-desc':
            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
          default:
            return 0;
        }
      });
    }

    return filtered;
  };

  const getFilteredClonedUrls = () => {
    let filtered = clonedUrls;

    // Apply search filter
    if (clonedUrlsSearchTerm) {
      filtered = filtered.filter(item => 
        item.originalUrl.toLowerCase().includes(clonedUrlsSearchTerm.toLowerCase()) ||
        item.clonedUrl.toLowerCase().includes(clonedUrlsSearchTerm.toLowerCase())
      );
    }

    // Apply sorting
    if (clonedUrlsSortBy) {
      filtered = [...filtered].sort((a, b) => {
        switch (clonedUrlsSortBy) {
          case 'original-asc':
            return a.originalUrl.localeCompare(b.originalUrl);
          case 'original-desc':
            return b.originalUrl.localeCompare(a.originalUrl);
          case 'cloned-asc':
            return a.clonedUrl.localeCompare(b.clonedUrl);
          case 'cloned-desc':
            return b.clonedUrl.localeCompare(a.clonedUrl);
          default:
            return 0;
        }
      });
    }

    return filtered;
  };

  // Pagination functions
  const getPaginatedUsers = () => {
    const filtered = getFilteredUsers();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getPaginatedAccounts = () => {
    const filtered = getFilteredAccounts();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getPaginatedClonedUrls = () => {
    const filtered = getFilteredClonedUrls();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = (filteredItems) => {
    return Math.ceil(filteredItems.length / itemsPerPage);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Navigation handlers
  const handleAccountClick = (account) => {
    navigate(`/admin/account-access/${account.shop}`);
  };

  const handleUserClick = (user) => {
    navigate(`/admin/user-accounts/${encodeURIComponent(user.email)}`);
  };

  const handleCreateAccountChange = (e) => {
    const { name, value } = e.target;
    
    // Prevent spaces in Shop ID field
    if (name === 'shop') {
      const sanitizedValue = value.replace(/\s/g, ''); // Remove all whitespace characters
      setCreateAccountData(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
    } else {
      setCreateAccountData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCreateUserChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setCreateUserData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked.toString() : value
      };
      
      // If role changed to developer, fetch dev page mapping
      if (name === 'role' && value === 'developer') {
        fetchAllDevPageMapping();
        // Default permissions will be set after mapping is loaded
        updated.dev_page_permissions = [];
      } else if (name === 'role' && value !== 'developer') {
        // Clear dev page permissions if role is not developer
        updated.dev_page_permissions = [];
        setAllDevPageMapping(null);
      }
      
      return updated;
    });
  };

  const handleDevPagePermissionChange = (pageName, checked) => {
    setCreateUserData(prev => {
      const currentPermissions = prev.dev_page_permissions || [];
      if (checked) {
        // Add page if not already in the list
        if (!currentPermissions.includes(pageName)) {
          return {
            ...prev,
            dev_page_permissions: [...currentPermissions, pageName]
          };
        }
      } else {
        // Remove page from the list
        return {
          ...prev,
          dev_page_permissions: currentPermissions.filter(page => page !== pageName)
        };
      }
      return prev;
    });
  };

  const handleCloneWebsiteChange = (e) => {
    const { name, value } = e.target;
    setCloneWebsiteData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If client is selected, fetch cloned URLs for that client
    // if (name === 'client' && value) {
    //   fetchClonedUrls(value);
    // } else if (name === 'client' && !value) {
    //   setClonedUrls([]);
    // }
  };

  const handlePermissionChange = (permission, checked) => {
    setCreateUserData(prev => ({
      ...prev,
      permissions: checked 
        ? [...prev.permissions, permission]
        : prev.permissions.filter(p => p !== permission)
    }));
  };





  const createAccount = async (e) => {
    e.preventDefault();
    try {
      setCreateAccountLoading(true);
      if (!createAccountData || !createAccountData.shop || !createAccountData.shop.trim() || createAccountData.shop.trim().length === 0) {
        setMessage({ type: 'error', text: 'Please enter a valid Shop ID' });
        return;
      }
      if (!createAccountData || !createAccountData.shop_name || !createAccountData.shop_name.trim() || createAccountData.shop_name.trim().length > 50) {
        setMessage({ type: 'error', text: 'Please enter a valid Shop Name' });
        return;
      }
      if (!createAccountData || !createAccountData.account_type || !['demo', 'live', 'secure'].includes(createAccountData.account_type)) {
        setMessage({ type: 'error', text: 'Please select a valid Account Type' });
        return;
      }
      if (!openAiChoiceComplete(createOpenAi)) {
        setMessage({ type: 'error', text: createOpenAi.mode === 'existing' ? 'Please choose the OpenAI project to use' : 'Please name the new OpenAI project' });
        return;
      }
      const response = await axios.post(app_url + '/api/cux/accounts', {
        shop: createAccountData.shop.trim(),
        shpat: "shpat_f71_sxi",
        old_data_last_run: "360",
        shop_name: createAccountData.shop_name.trim(),
        account_type: createAccountData.account_type,
        // Optional. Without it the account is created with no owner, and the
        // customer has to name one from Settings → Teams before they can reach
        // billing. Staff must never be the owner of a customer's account.
        owner_email: (createAccountData.owner_email || '').trim(),
        // Null = the shared default project.
        openai_project: openAiChoiceBody(createOpenAi)
      }, { withCredentials: true });

      const data = response.data;
      if (data.success) {
        // The account is created either way, but ownership is best-effort: if
        // the named owner has no user account there is nobody to make one, and
        // saying only "created successfully" is how an account silently ends up
        // with no one who can reach its billing.
        const warnings = [
          data.ownerWarning ? `No owner was set. ${data.ownerWarning}` : null,
          data.openAiProjectWarning || null,
        ].filter(Boolean);
        setMessage(warnings.length
          ? { type: 'warning', text: `Account created, with a warning. ${warnings.join(' ')}` }
          : { type: 'success', text: 'Account created successfully! Redirecting to Account Management...' });
        setCreateAccountData({ shop: '', shop_name: '', account_type: 'live', owner_email: '' });
        setCreateOpenAi({ mode: 'default', projectId: '', projectName: '' });
        setOpenAiRefresh((n) => n + 1);
        // Refresh data to update Redis cache and local state
        await fetchData();
        // Redirect to Account Management page after 1.5 seconds
        setTimeout(() => {
          setActiveTab('create-account');
        }, 1500);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to create account' });
      }
    } catch (error) {
      const serverMsg = error?.response?.data?.message;
      setMessage({ type: 'error', text: serverMsg || 'Error creating account' });
    } finally {
      setCreateAccountLoading(false);
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    
    // Validate email before submitting
    if (!createUserData.email || !/\S+@\S+\.\S+/.test(createUserData.email)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }
    
    try {
      setCreateUserLoading(true);
      const requestData = {
        email: createUserData.email,
        role: createUserData.role,
        permissions: createUserData.permissions
      };
      
      // Add dev_page_permissions if role is developer
      if (createUserData.role === 'developer' && createUserData.dev_page_permissions) {
        requestData.dev_page_permissions = createUserData.dev_page_permissions;
      }
      
      const response = await axios.post(app_url + '/api/admin/create-user', requestData, { withCredentials: true });

      const data = response.data;
      if (data.success) {
        setMessage({ type: 'success', text: `User created successfully! Password: ${data.password}, Code: ${data.code}. Redirecting to User Management...` });
        setCreateUserData({ email: '', role: 'user', permissions: [], dev_page_permissions: [] });
        setAllDevPageMapping(null);
        // Refresh data to update Redis cache and local state
        await fetchData();
        // Redirect to User Management page after 1.5 seconds
        setTimeout(() => {
          setActiveTab('create-user');
        }, 1500);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to create user' });
      }
    } catch (error) {
      const serverMsg = error?.response?.data?.message;
      setMessage({ type: 'error', text: serverMsg || 'Error creating user' });
    } finally {
      setCreateUserLoading(false);
    }
  };

  const cloneWebsite = async (e) => {
    e.preventDefault();
    try {
      setCloneWebsiteLoading(true);
      const response = await axios.post(app_url + '/api/admin/clone-website', {
        url: cloneWebsiteData.url,
        client: cloneWebsiteData.client,
        selectedCloner: cloneWebsiteData.selectedCloner
      }, { withCredentials: true });

      const data = response.data;
      if (data.success) {
        const previewUrl = data.data?.previewUrl || data.previewUrl;
        const downloadUrl = data.data?.downloadUrl || data.downloadUrl;
        const tip = data.data?.tip || data.tip;
        const client = data.client;
        
        // Find the client name for display
        const selectedClient = stores.find(store => store.shop === client);
        const clientName = selectedClient ? (selectedClient.shop_name || selectedClient.shop) : client;
        
        let successMessage = `Website cloned successfully for ${clientName}!`;
        
         setMessage({ type: 'success', text: successMessage });
         setCloneWebsiteData({ url: '', client: '', selectedCloner: 'standard' });
         setShowCloneWebsiteForm(false);
         // Refresh stores data to update Redis cache and local state
         await fetchData();
         // Auto-select the client and refresh cloned URLs
         if (client) {
           setSelectedClientForUrls(client);
           await fetchClonedUrls(client);
         }
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to clone website' });
      }
    } catch (error) {
      const serverMsg = error?.response?.data?.message;
      setMessage({ type: 'error', text: serverMsg || 'Error cloning website' });
    } finally {
      setCloneWebsiteLoading(false);
    }
  };



  // Render pagination component
  const renderPagination = (totalItems, currentPage, onPageChange) => {
    const totalPages = getTotalPages(totalItems);
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <Pagination.Prev
        key="prev"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <Pagination.Item key={1} onClick={() => onPageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        pages.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => onPageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      pages.push(
        <Pagination.Item key={totalPages} onClick={() => onPageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    // Next button
    pages.push(
      <Pagination.Next
        key="next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );

    return (
      <div className="admin-pagination-container">
        <Pagination className="admin-pagination">
          {pages}
        </Pagination>
        <div className="admin-pagination-info">
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
        </div>
      </div>
    );
  };

  const renderCreateAccount = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h3 className="admin-section-title">Accounts</h3>
        <Button 
          variant={showCreateAccountForm ? "outline-secondary" : "primary"}
          onClick={() => setShowCreateAccountForm(!showCreateAccountForm)}
          className="admin-create-toggle-btn"
        >
          {showCreateAccountForm ? 'Cancel Create Account' : 'Create New Account'}
        </Button>
      </div>
      
      {/* Create Account Form */}
      {showCreateAccountForm && (
        <div className="admin-form-section">
          <h4 className="admin-subsection-title">Create New Account</h4>
          <Form onSubmit={createAccount} className="admin-form">
            <FormGroup className="mb-3">
              <FormLabel className="admin-label">Shop ID:</FormLabel>
              <FormControl
                type="text"
                name="shop"
                value={createAccountData.shop}
                onChange={handleCreateAccountChange}
                required
                placeholder="Enter Shop ID"
                className="admin-input"
              />
            </FormGroup>

            <FormGroup className="mb-3">
              <FormLabel className="admin-label">Shop Name:</FormLabel>
              <FormControl
                type="text"
                name="shop_name"
                value={createAccountData.shop_name}
                onChange={handleCreateAccountChange}
                required
                maxLength={50}
                placeholder="Enter shop display name"
                className="admin-input"
              />
            </FormGroup>

            <FormGroup className="mb-3">
              <FormLabel className="admin-label">Account Type:</FormLabel>
              <FormControl
                as="select"
                name="account_type"
                value={createAccountData.account_type}
                onChange={handleCreateAccountChange}
                required
                className="admin-input"
              >
                <option value="demo">Demo</option>
                <option value="live">Live</option>
                <option value="secure">Secure</option>
              </FormControl>
            </FormGroup>

            <FormGroup className="mb-3">
              <FormLabel className="admin-label">Owner Email (optional):</FormLabel>
              <FormControl
                type="email"
                name="owner_email"
                value={createAccountData.owner_email}
                onChange={handleCreateAccountChange}
                placeholder="founder@company.com"
                className="admin-input"
              />
              <small className="text-muted">
                Must already have a user account. They become the account&apos;s owner — the only person
                who can transfer its ownership. Leave blank and the account is created with no owner.
              </small>
            </FormGroup>

            <CreateAccountOpenAiField appUrl={app_url} value={createOpenAi} onChange={setCreateOpenAi} accountName={createAccountData.shop_name.trim()} />

            <Button
              type="submit" 
              disabled={createAccountLoading} 
              className="admin-btn"
            >
              {createAccountLoading ? 'Creating...' : 'Create Account'}
            </Button>
          </Form>
        </div>
      )}

      {/* Existing Accounts List */}
      <div className="admin-list-section">
        <h4 className="admin-subsection-title">Existing Accounts ({getFilteredAccounts().length})</h4>
        
        {/* Search and Filter Controls */}
        <Row className="admin-controls-row">
          <Col md={4}>
            <InputGroup className="admin-search-group">
              <InputGroup.Text className="admin-search-icon">🔍</InputGroup.Text>
              <FormControl
                type="text"
                placeholder="Search accounts..."
                value={accountSearchTerm}
                onChange={(e) => setAccountSearchTerm(e.target.value)}
                className="admin-search-input"
              />
            </InputGroup>
          </Col>
          <Col md={4}>
            <FormControl
              as="select"
              value={accountTypeFilter}
              onChange={(e) => setAccountTypeFilter(e.target.value)}
              className="admin-sort-select"
            >
              <option value="">All Account Types</option>
              <option value="demo">Demo</option>
              <option value="live">Live</option>
              <option value="secure">Secure</option>
            </FormControl>
          </Col>
          <Col md={4}>
            <FormControl
              as="select"
              value={accountSortBy}
              onChange={(e) => setAccountSortBy(e.target.value)}
              className="admin-sort-select"
            >
              <option value="">Sort by</option>
              <option value="name-asc">Client Name (A-Z)</option>
              <option value="name-desc">Client Name (Z-A)</option>
              <option value="features-asc">Features (Asc)</option>
              <option value="features-desc">Features (Desc)</option>
              <option value="created-asc">Created (Asc)</option>
              <option value="created-desc">Created (Desc)</option>
            </FormControl>
          </Col>
        </Row>

        {getFilteredAccounts().length === 0 ? (
          <p className="admin-no-data">
            {accountSearchTerm ? 'No accounts match your search criteria' : 'No accounts found'}
          </p>
        ) : (
          <>
            <ListGroup className="admin-list-group">
              {getPaginatedAccounts().map((store, index) => (
                <ListGroupItem 
                  key={index} 
                  className="admin-list-item clickable"
                  onClick={() => handleAccountClick(store)}
                >
                  <div className="admin-list-item-content">
                    <div className="admin-list-item-main">
                      <strong>{store.shop_name || store.shop}</strong>
                      <span className="admin-list-item-subtitle">{store.shop}</span>
                      <span className="admin-list-item-type">
                        Type: <span className={`account-type-badge account-type-${store.account_type || 'live'}`}>
                          {(store.account_type || 'live').charAt(0).toUpperCase() + (store.account_type || 'live').slice(1)}
                        </span>
                      </span>
                      <span className="admin-list-item-subtitle" data-testid="admin-account-openai-project">
                        OpenAI: {openAiAssignments[normalizeAccountShop(store.shop)]?.projectName || openAiAssignments[normalizeAccountShop(store.shop)]?.projectId || 'Default project'}
                      </span>
                    </div>
                    <div className="admin-list-item-actions">
                      <span className="admin-list-item-status">
                        {store.allowed_pages ? Object.keys(store.allowed_pages).filter(page => store.allowed_pages[page]).length : 0} features enabled
                      </span>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={(e) => { e.stopPropagation(); setOpenAiDialogAccount(store); }}
                        data-testid="admin-account-openai-change"
                      >
                        OpenAI project
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline-primary"
                        onClick={() => handleAccountClick(store)}
                      >
                        Manage Access
                      </Button>
                    </div>
                  </div>
                </ListGroupItem>
              ))}
            </ListGroup>
            
            {/* Pagination */}
            {renderPagination(getFilteredAccounts().length, currentPage, handlePageChange)}
          </>
        )}
      </div>
      {openAiDialogAccount && (
        <OpenAiProjectDialog
          appUrl={app_url}
          account={openAiDialogAccount}
          onClose={() => setOpenAiDialogAccount(null)}
          onSaved={(data) => {
            setOpenAiDialogAccount(null);
            setOpenAiRefresh((n) => n + 1);
            setMessage({ type: 'success', text: data?.mode === 'dedicated' ? `${openAiDialogAccount.shop_name || openAiDialogAccount.shop} now runs on OpenAI project ${data.projectName || data.projectId}.` : `${openAiDialogAccount.shop_name || openAiDialogAccount.shop} now runs on the default OpenAI project.` });
          }}
        />
      )}
    </div>
  );

  const renderClonedUrlsTable = () => {
    if (clonedUrlsLoading) {
      return (
        <div className="admin-loading-inline">
          <div className="admin-loading-content">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading cloned URLs...</p>
          </div>
        </div>
      );
    }

    const filteredUrls = getFilteredClonedUrls();
    
    if (filteredUrls.length === 0) {
      return (
        <p className="admin-no-data">
          {clonedUrlsSearchTerm ? 'No cloned websites match your search criteria' : 'No cloned websites found for this client'}
        </p>
      );
    }

    return (
      <>
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Original URL</th>
                <th>Cloned URL</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedClonedUrls().map((item, index) => (
                <tr key={index} className={index % 2 === 0 ? 'admin-table-row-even' : 'admin-table-row-odd'}>
                  <td>
                    <a 
                      href={item.originalUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                    >
                      {item.originalUrl}
                    </a>
                  </td>
                  <td>
                    <a 
                      href={item.clonedUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-decoration-none"
                    >
                      {item.clonedUrl}
                    </a>
                  </td>
                  <td>
                    <Button 
                      size="sm" 
                      variant="outline-primary"
                      onClick={() => window.open(item.clonedUrl, '_blank')}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {renderPagination(filteredUrls.length, currentPage, handlePageChange)}
      </>
    );
  };

  const renderCloneWebsite = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h3 className="admin-section-title">Clone Website</h3>
        <Button 
          variant={showCloneWebsiteForm ? "outline-secondary" : "primary"}
          onClick={() => setShowCloneWebsiteForm(!showCloneWebsiteForm)}
          className="admin-create-toggle-btn"
        >
          {showCloneWebsiteForm ? 'Cancel Clone Website' : 'Clone New Website'}
        </Button>
      </div>
      
      {/* Clone Website Form */}
      {showCloneWebsiteForm && (
        <div className="admin-form-section">
          <h4 className="admin-subsection-title">Clone Website</h4>
          <Form onSubmit={cloneWebsite} className="admin-form">
            <FormGroup className="mb-3">
              <FormLabel className="admin-label">Select Client:</FormLabel>
              <FormControl
                as="select"
                name="client"
                value={cloneWebsiteData.client}
                onChange={handleCloneWebsiteChange}
                required
                className="admin-input"
              >
                <option value="">Choose a client...</option>
                {stores.map((store) => (
                  <option key={store.shop} value={store.shop}>
                    {store.shop_name || store.shop}
                  </option>
                ))}
              </FormControl>
            </FormGroup>

            <FormGroup className="mb-3">
              <FormLabel className="admin-label">Clone Type:</FormLabel>
              <div className="admin-clone-type-compact">
                <div className="admin-clone-type-item">
                  <input
                    type="radio"
                    name="selectedCloner"
                    id="standard-clone"
                    value="standard"
                    checked={cloneWebsiteData.selectedCloner === 'standard'}
                    onChange={handleCloneWebsiteChange}
                    className="admin-clone-checkbox"
                  />
                  <label 
                    htmlFor="standard-clone" 
                    className="admin-clone-type-label"
                    title="Best for regular websites with standard HTML/CSS structure"
                  >
                    <span className="admin-clone-type-title">Standard Clone</span>
                  </label>
                </div>

                <div className="admin-clone-type-item">
                  <input
                    type="radio"
                    name="selectedCloner"
                    id="ecommerce-clone"
                    value="ecommerce"
                    checked={cloneWebsiteData.selectedCloner === 'ecommerce'}
                    onChange={handleCloneWebsiteChange}
                    className="admin-clone-checkbox"
                  />
                  <label 
                    htmlFor="ecommerce-clone" 
                    className="admin-clone-type-label"
                    title="Optimized for e-commerce platforms like Shopify, WooCommerce, Magento, and other online stores"
                  >
                    <span className="admin-clone-type-title">E-commerce Clone</span>
                  </label>
                </div>

                <div className="admin-clone-type-item">
                  <input
                    type="radio"
                    name="selectedCloner"
                    id="cms-clone"
                    value="cms"
                    checked={cloneWebsiteData.selectedCloner === 'cms'}
                    onChange={handleCloneWebsiteChange}
                    className="admin-clone-checkbox"
                  />
                  <label 
                    htmlFor="cms-clone" 
                    className="admin-clone-type-label"
                    title="Optimized for content management systems like WordPress, Drupal, Joomla, and other CMS platforms"
                  >
                    <span className="admin-clone-type-title">CMS Clone</span>
                  </label>
                </div>

                <div className="admin-clone-type-item">
                  <input
                    type="radio"
                    name="selectedCloner"
                    id="nextjs-clone"
                    value="nextjs"
                    checked={cloneWebsiteData.selectedCloner === 'nextjs'}
                    onChange={handleCloneWebsiteChange}
                    className="admin-clone-checkbox"
                  />
                  <label 
                    htmlFor="nextjs-clone" 
                    className="admin-clone-type-label"
                    title="Optimized for modern JavaScript frameworks like React, Next.js, Vue.js, Angular, and other SPA frameworks"
                  >
                    <span className="admin-clone-type-title">Next.js Clone</span>
                  </label>
                </div>

                <div className="admin-clone-type-item">
                  <input
                    type="radio"
                    name="selectedCloner"
                    id="abs-clone"
                    value="abs"
                    checked={cloneWebsiteData.selectedCloner === 'abs'}
                    onChange={handleCloneWebsiteChange}
                    className="admin-clone-checkbox"
                  />
                  <label 
                    htmlFor="abs-clone" 
                    className="admin-clone-type-label"
                    title="Custom advanced build systems with php"
                  >
                    <span className="admin-clone-type-title">Lazy Loading Clone</span>
                  </label>
                </div>

                <div className="admin-clone-type-item">
                  <input
                    type="radio"
                    name="selectedCloner"
                    id="preloader-clone"
                    value="preloader"
                    checked={cloneWebsiteData.selectedCloner === 'preloader'}
                    onChange={handleCloneWebsiteChange}
                    className="admin-clone-checkbox"
                  />
                  <label 
                    htmlFor="preloader-clone" 
                    className="admin-clone-type-label"
                    title="Sites with heavy JS loaders"
                  >
                    <span className="admin-clone-type-title">Preloader Clone</span>
                  </label>
                </div>
              </div>
            </FormGroup>

            <FormGroup className="mb-3">
              <FormLabel className="admin-label">Website URL:</FormLabel>
              <FormControl
                type="url"
                name="url"
                value={cloneWebsiteData.url}
                onChange={handleCloneWebsiteChange}
                required
                inputMode="url"
                placeholder="Enter website URL to clone (e.g., https://example.com)"
                className="admin-input"
              />
            </FormGroup>
            <Button 
              type="submit" 
              disabled={cloneWebsiteLoading} 
              className="admin-btn"
            >
              {cloneWebsiteLoading ? 'Cloning...' : 'Clone Website'}
            </Button>
          </Form>
        </div>
      )}

      {/* Cloned URLs Table */}
      <div className="admin-list-section">
        <h4 className="admin-subsection-title">
          Cloned Websites {selectedClientForUrls ? `(${getFilteredClonedUrls().length})` : ''}
        </h4>
        
        {/* Client Selection */}
        <Row className="admin-controls-row">
          <Col md={6}>
            <FormGroup className="mb-3">
              <FormLabel className="admin-label">Select Client:</FormLabel>
              <FormControl
                as="select"
                value={selectedClientForUrls}
                onChange={(e) => handleClientSelectionForUrls(e.target.value)}
                className="admin-input"
              >
                <option value="">Choose a client to view cloned websites...</option>
                {stores.map((store) => (
                  <option key={store.shop} value={store.shop}>
                    {store.shop_name || store.shop}
                  </option>
                ))}
              </FormControl>
            </FormGroup>
          </Col>
        </Row>

        {!selectedClientForUrls ? (
          <p className="admin-no-data">
            Select a client to view their cloned websites
          </p>
        ) : (
          <>
            {/* Search and Sort Controls */}
            <Row className="admin-controls-row">
              <Col md={6}>
                <InputGroup className="admin-search-group">
                  <InputGroup.Text className="admin-search-icon">🔍</InputGroup.Text>
                  <FormControl
                    type="text"
                    placeholder="Search cloned websites..."
                    value={clonedUrlsSearchTerm}
                    onChange={(e) => setClonedUrlsSearchTerm(e.target.value)}
                    className="admin-search-input"
                  />
                </InputGroup>
              </Col>
              <Col md={6}>
                <FormControl
                  as="select"
                  value={clonedUrlsSortBy}
                  onChange={(e) => setClonedUrlsSortBy(e.target.value)}
                  className="admin-sort-select"
                >
                  <option value="">Sort by</option>
                  <option value="original-asc">Original URL (A-Z)</option>
                  <option value="original-desc">Original URL (Z-A)</option>
                  <option value="cloned-asc">Cloned URL (A-Z)</option>
                  <option value="cloned-desc">Cloned URL (Z-A)</option>
                </FormControl>
              </Col>
            </Row>

            {renderClonedUrlsTable()}
          </>
        )}
      </div>
    </div>
  );

  const renderCreateUser = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h3 className="admin-section-title">Users</h3>
        <Button 
          variant={showCreateUserForm ? "outline-secondary" : "primary"}
          onClick={() => setShowCreateUserForm(!showCreateUserForm)}
          className="admin-create-toggle-btn"
        >
          {showCreateUserForm ? 'Cancel Create User' : 'Create New User'}
        </Button>
      </div>
      
      {/* Create User Form */}
      {showCreateUserForm && (
        <div className="admin-form-section">
          <h4 className="admin-subsection-title">Create New User</h4>
          <Row>
            <Col md={createUserData.role === 'developer' ? 6 : 12}>
              <Form onSubmit={createUser} className="admin-form">
                <FormGroup className="mb-3">
                  <FormLabel className="admin-label">Email:</FormLabel>
                  <FormControl
                    type="email"
                    name="email"
                    value={createUserData.email}
                    onChange={handleCreateUserChange}
                    required
                    placeholder="Enter user email"
                    className="admin-input"
                  />
                </FormGroup>

                <FormGroup className="mb-3">
                  <FormLabel className="admin-label">Role:</FormLabel>
                  <FormControl
                    as="select"
                    name="role"
                    value={createUserData.role}
                    onChange={handleCreateUserChange}
                    required
                    className="admin-input"
                  >
                    {availableRoles.map(role => (
                      <option key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </option>
                    ))}
                  </FormControl>
                </FormGroup>

                {/* <FormGroup className="mb-3">
                  <FormLabel className="admin-label">Permissions:</FormLabel>
                  <div className="admin-pages-grid">
                    {availablePermissions.map(permission => (
                      <FormCheck
                        key={permission}
                        type="checkbox"
                        checked={createUserData.permissions.includes(permission)}
                        onChange={(e) => handlePermissionChange(permission, e.target.checked)}
                        label={permission.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        className="admin-checkbox"
                      />
                    ))}
                  </div>
                </FormGroup> */}

                <Button 
                  type="submit" 
                  disabled={createUserLoading} 
                  className="admin-btn"
                >
                  {createUserLoading ? 'Creating...' : 'Create User'}
                </Button>
              </Form>
            </Col>
            
            {/* Dev Page Permissions Panel - Show only when role is developer */}
            {createUserData.role === 'developer' && (
              <Col md={6}>
                <div className="admin-dev-permissions-panel">
                  <h5 className="admin-subsection-title">Dev Page Permissions</h5>
                  {devPageMappingLoading ? (
                    <div className="admin-loading-inline">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading dev page mapping...</p>
                    </div>
                  ) : allDevPageMapping ? (
                    <div className="admin-dev-mapping-info">
                      <p className="text-muted mb-3">
                        <small>Select Pages for Dev Access:</small>
                      </p>
                      {Object.keys(allDevPageMapping).length === 0 ? (
                        <p className="text-muted">No pages available in dev page mapping</p>
                      ) : (
                        <div className="admin-accounts-grid">
                          {Object.keys(allDevPageMapping).map((pageName) => (
                            <div key={pageName} className="admin-account-item">
                              <FormCheck
                                type="checkbox"
                                id={`dev-permission-${pageName}`}
                                checked={createUserData.dev_page_permissions?.includes(pageName) || false}
                                onChange={(e) => handleDevPagePermissionChange(pageName, e.target.checked)}
                                label={pageName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                className="admin-checkbox-large"
                              />
                              <div className="admin-account-details">
                                <div className="admin-account-shop">{pageName}</div>
                                <div className="admin-account-features">
                                  Page ID: {allDevPageMapping[pageName]}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-muted">
                      <p>Dev page mapping not found in profit_x_settings collection</p>
                    </div>
                  )}
                </div>
              </Col>
            )}
          </Row>
        </div>
      )}

      {/* Existing Users List */}
      <div className="admin-list-section">
        <h4 className="admin-subsection-title">Existing Users ({getFilteredUsers().length})</h4>
        
        {/* Search and Filter Controls */}
        <Row className="admin-controls-row">
          <Col md={6}>
            <InputGroup className="admin-search-group">
              <InputGroup.Text className="admin-search-icon">🔍</InputGroup.Text>
              <FormControl
                type="text"
                placeholder="Search users..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                className="admin-search-input"
              />
            </InputGroup>
          </Col>
          <Col md={6}>
            <FormControl
              as="select"
              value={userSortBy}
              onChange={(e) => setUserSortBy(e.target.value)}
              className="admin-sort-select"
            >
              <option value="">Sort by</option>
              <option value="email-asc">Email (A-Z)</option>
              <option value="email-desc">Email (Z-A)</option>
              <option value="role-asc">Role (A-Z)</option>
              <option value="role-desc">Role (Z-A)</option>
              <option value="accounts-asc">Accounts (Asc)</option>
              <option value="accounts-desc">Accounts (Desc)</option>
            </FormControl>
          </Col>
        </Row>

        {getFilteredUsers().length === 0 ? (
          <p className="admin-no-data">
            {userSearchTerm ? 'No users match your search criteria' : 'No users found'}
          </p>
        ) : (
          <>
            <ListGroup className="admin-list-group">
              {getPaginatedUsers().map((user, index) => (
                <ListGroupItem 
                  key={index} 
                  className="admin-list-item clickable"
                  onClick={() => handleUserClick(user)}
                >
                  <div className="admin-list-item-content">
                    <div className="admin-list-item-main">
                      <strong>{user.email}</strong>
                      <span className="admin-list-item-subtitle">{user.role || 'user'}</span>
                    </div>
                    <div className="admin-list-item-actions">
                      <span className="admin-list-item-status">
                        {user.stores ? user.stores.length : 0} accounts assigned
                      </span>
                      <Button 
                        size="sm" 
                        variant="outline-primary"
                        onClick={() => handleUserClick(user)}
                      >
                        Manage Accounts
                      </Button>
                    </div>
                  </div>
                </ListGroupItem>
              ))}
            </ListGroup>
            
            {/* Pagination */}
            {renderPagination(getFilteredUsers().length, currentPage, handlePageChange)}
          </>
        )}
      </div>
    </div>
  );

  const renderSectionRearrangement = () => (
    <div className="admin-section">
      <div className="admin-section-header">
        <h3 className="admin-section-title">Section Rearrangement Tool</h3>
        <p className="admin-section-description">
          Advanced webpage screenshot generator with AI analysis for section rearrangement and optimization.
        </p>
      </div>
      
      <div className="admin-tool-redirect">
        <div className="admin-tool-info">
          <h4 className="admin-subsection-title">CognitiveUX AI Tool</h4>
          <p className="admin-tool-description">
            This powerful tool allows you to:
          </p>
          <ul className="admin-tool-features">
            <li>Capture screenshots of webpage sections using CSS selectors</li>
            <li>Generate AI-powered descriptions for selected sections</li>
            <li>Analyze and improve code for better responsiveness</li>
            <li>Preview sections across different device sizes</li>
            <li>Get AI suggestions for visual improvements</li>
          </ul>
          
          <div className="admin-tool-actions">
            <Button 
              variant="primary" 
              size="lg"
              onClick={() => window.open('https://apps.cognitiveux.ai/', '_blank')}
              className="admin-btn admin-btn-primary"
            >
              Open Section Rearrangement Tool
            </Button>
            <p className="admin-tool-note">
              <small className="text-muted">
                The tool will open in a new tab. Make sure to allow pop-ups for this site.
              </small>
            </p>
          </div>
        </div>
      </div>
    </div>
  );





  const fetchAnalyticsLandingPages = async (shop) => {
    if (!shop) return;
    setAnalyticsLPLoading(true);
    try {
      const res = await axios.get(`${app_url}/api/admin/analytics-extension/landing-pages?shop=${encodeURIComponent(shop)}`, { withCredentials: true });
      setAnalyticsLandingPages(res.data?.data || []);
    } catch (err) {
      toast.error('Failed to fetch landing pages');
    } finally {
      setAnalyticsLPLoading(false);
    }
  };

  const fetchEnabledAnalyticsExtensions = async (shop) => {
    if (!shop) return;
    try {
      const res = await axios.get(`${app_url}/api/admin/analytics-extension?shop=${encodeURIComponent(shop)}`, { withCredentials: true });
      setEnabledAnalyticsExtensions(res.data?.data || { is_active: false, config_id: null });
    } catch (err) {
      toast.error('Failed to fetch enabled analytics extensions');
    }
  };

  const handleAnalyticsShopChange = (shop) => {
    setAnalyticsShop(shop);
    setEnabledAnalyticsExtensions({ is_active: false, config_id: null });
    if (shop) {
      fetchEnabledAnalyticsExtensions(shop);
    }
  };

  const handleEnableAnalyticsExtensions = async () => {
    if (!analyticsShop) return;
    setAnalyticsExtensionLoading(true);
    try {
      const res = await axios.post(`${app_url}/api/admin/analytics-extension`, {
        shop: analyticsShop
      }, { withCredentials: true });
      const { successful = [], failed = [] } = res.data;
      if (successful.length > 0) toast.success('Analytics extension enabled for account');
      if (failed.length > 0) toast.error('Failed to enable analytics extension');
      fetchEnabledAnalyticsExtensions(analyticsShop);
    } catch (err) {
      toast.error('Failed to enable analytics extension');
    } finally {
      setAnalyticsExtensionLoading(false);
    }
  };

  const handleDisableAnalyticsExtension = async (config_id) => {
    try {
      await axios.delete(`${app_url}/api/admin/analytics-extension/${config_id}`, {
        data: { shop: analyticsShop },
        withCredentials: true
      });
      toast.success('Analytics extension disabled');
      fetchEnabledAnalyticsExtensions(analyticsShop);
    } catch (err) {
      toast.error('Failed to disable analytics extension');
    }
  };

  const renderEnableAnalyticsExtension = () => {
    const isEnabled = enabledAnalyticsExtensions.is_active === true;
    const configId = enabledAnalyticsExtensions.config_id;
    return (
      <div className="admin-section">
        <div className="admin-section-header">
          <h3 className="admin-section-title">Enable Analytics Extension</h3>
          <p className="admin-section-description">
            Enable or disable the analytics extension at the account level.
          </p>
        </div>

        <div className="d-flex align-items-end gap-3 flex-wrap">
          <FormGroup style={{ minWidth: '240px', flex: '1 1 240px', maxWidth: '320px' }}>
            <FormLabel className="admin-form-label">Select Shop</FormLabel>
            <Form.Select
              value={analyticsShop}
              onChange={(e) => handleAnalyticsShopChange(e.target.value)}
              className="admin-form-control"
            >
              <option value="">-- Select a shop --</option>
              {stores.map((store) => (
                <option key={store.shop} value={store.shop}>
                  {store.shop_name || store.shop}
                </option>
              ))}
            </Form.Select>
          </FormGroup>

          {analyticsShop && (
            <FormGroup className="mb-0">
              <FormLabel className="admin-form-label">Status</FormLabel>
              <div style={{ paddingBottom: '7px' }}>
                {isEnabled ? (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                    borderRadius: '20px', padding: '4px 12px', fontSize: '13px', fontWeight: 600
                  }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                    Enabled
                  </span>
                ) : (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: '#f8f9fa', color: '#6b7280', border: '1px solid #e5e7eb',
                    borderRadius: '20px', padding: '4px 12px', fontSize: '13px', fontWeight: 600
                  }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#9ca3af', display: 'inline-block' }} />
                    Disabled
                  </span>
                )}
              </div>
            </FormGroup>
          )}

          {analyticsShop && (
            <FormGroup className="mb-0">
              <FormLabel className="admin-form-label d-block" style={{ visibility: 'hidden' }}>Action</FormLabel>
              {isEnabled ? (
                <Button
                  variant="outline-danger"
                  className="admin-btn"
                  style={{ borderRadius: '8px', fontWeight: 600, fontSize: '13px', padding: '6px 18px' }}
                  onClick={() => handleDisableAnalyticsExtension(configId)}
                  disabled={analyticsExtensionLoading}
                >
                  {analyticsExtensionLoading ? 'Disabling...' : 'Disable'}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  className="admin-btn admin-btn-primary"
                  style={{ borderRadius: '8px', fontWeight: 600, fontSize: '13px', padding: '6px 18px' }}
                  onClick={handleEnableAnalyticsExtensions}
                  disabled={analyticsExtensionLoading}
                >
                  {analyticsExtensionLoading ? 'Enabling...' : 'Enable'}
                </Button>
              )}
            </FormGroup>
          )}
        </div>
      </div>
    );
  };

  // Check if user has admin access
  if (!isAdmin() && !hasPermission('write_users')) {
    return (
      <div className="main-container">
        <div className="admin-header">
          <h1 className="admin-title">Access Denied</h1>
          <p className="admin-subtitle">You don't have permission to access the admin dashboard</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="admin-header">
        <h1 className="admin-title">Admin Dashboard</h1>
        <p className="admin-subtitle">Manage accounts, users, and access control</p>
      </div>

      {message.text && (
        <div className={`admin-message admin-message-${message.type}`}>
          {message.text}
          <Button 
            variant="link" 
            className="admin-message-close"
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </Button>
        </div>
      )}

      <Container fluid className="p-0">
        <div className="admin-tabs-header my-4">
          <Nav className="admin-tabs-header">
            {adminSections.map((section) => (
              <Nav.Item key={section.id}>
                <Nav.Link 
                  className={activeTab === section.id ? 'active' : ''}
                  onClick={() => handleTabChange(section.id)}
                >
                  {section.title}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>

        {(fetchingData || pagesLoading) ? (
          <div className="admin-loading-inline">
            <div className="admin-loading-content">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">
                {fetchingData ? 'Fetching data from database...' : 'Loading available pages...'}
              </p>
            </div>
          </div>
        ) : (
          <div className="admin-content">
            {adminSections.map((section) => (
              <div 
                key={section.id} 
                className={`admin-tab-content ${activeTab === section.id ? 'active' : 'hidden'}`}
              >
                {section.component === 'accounts' && renderCreateAccount()}
                {section.component === 'users' && renderCreateUser()}
                {section.component === 'clone-website' && renderCloneWebsite()}
                {section.component === 'pricing-plans' && (
                  <PricingPlanManagement ViewedPage={ViewedPage} app_url={app_url} embedded={true} />
                )}
                {section.component === 'prebuilt-agents' && activeTab === 'prebuilt-agents' && <PublishManagerModal embedded={true} />}
                {section.component === 'workspace-agents' && activeTab === 'workspace-agents' && <WorkspaceAgentsManager />}
                {section.component === 'mcp-tools' && activeTab === 'mcp-tools' && <McpServerToolsManager />}
                {section.component === 'canvas-templates' && activeTab === 'canvas-templates' && <CanvasPublishManager />}
                {/* activeTab guard matters: every section div is always rendered
                    (CSS-hidden), and without it this panel would fire its
                    /admin/* fetches on every Admin Dashboard visit. */}
                {section.component === 'default-data-sources' && activeTab === 'default-data-sources' && <DefaultSourcesManager />}
                {section.component === 'enable-analytics-extension' && renderEnableAnalyticsExtension()}
                {section.component === 'ab-testing' && activeTab === 'ab-testing' && <AbPasscodeManager app_url={app_url} />}
              </div>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
};

export default AdminDashboard;
